import { Component, Optional } from "@angular/core"
import { DynamicItem } from "./dynamic-item"
import { AttributeModel } from "./model/attribute.model"
import { ValidationStrategyFactory } from "src/app/strategy/validation-strategy.factory"
import { CurrencyService } from "src/app/service/currency.service";
import { MaskApplierService } from 'ngx-mask';

@Component({
    template: ''
})
export abstract class ComponentBase extends DynamicItem {

    protected currencyService: CurrencyService;
    protected maskService?: MaskApplierService;

    constructor(@Optional() maskService?: MaskApplierService) {
        super()
        this.maskService = maskService;
        this.currencyService = new CurrencyService(maskService);
    }

    get disabled(): boolean {
        let disabled = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "disabled")
        return disabled && disabled.value.toLowerCase() == "true"
    }

    get showTitle(): boolean {
        let hideOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "hidetitle")

        if(hideOpt) {
            return hideOpt.value.toLowerCase() == "false"
        }
        return true
    }

    protected formatDateToBR(date: Date): string {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
    
      return `${day}/${month}/${year}`;
    }

    protected getDocumentMask(attribute: AttributeModel): string {
        if(attribute.value && attribute.value.length > 11) {
            return "00.000.000/0000-00"
        } else {
            return "000.000.000-00**"
        }
    }

    protected getCurrencyMaskText(attribute: AttributeModel, isInputText: boolean = false): string {
        return this.currencyService.getCurrencyMaskText(attribute, isInputText);
    }

    getKeyForIdentifier(key: string): string {
        if(key) {
            let formattedString = key.replace(/ /g, "_");
            formattedString = formattedString.replace(/[^a-z0-9_]/gi, "");
        
            return formattedString;
        } else {
            return ""
        }
    }

    getOptionByKey(attribute: AttributeModel, key: string, defaultValue: string = ""): string {
        if (!attribute.options) {
            return "";
        }

        let opt = attribute.options
            .find(option => option.key.toLowerCase() == key.toLowerCase());

        if (opt) {
            return opt.value;
        }
        else {
            return defaultValue;
        }
    }

    protected getPhoneMask(attribute: AttributeModel, isInputText: boolean = false): string {
      if(isInputText) {
        return this.getPhoneMaskForInput();
      }

      if(attribute.value && attribute.value.length > 10) {
          return "(00) 00000-0000"
      } else {
          return "(00) 0000-0000*"
      }
    }

    private getPhoneMaskForInput(): string {
      return "(00) 00000-0000||(00) 0000-0000*"
    }

    getSizeClass(attribute: AttributeModel): string {
        if (!attribute.options) {
          return "col-md-12"
        }

        let size = attribute.options
            .find(option => option.key == "size-class")

        if (size) {
            return size.value
        }else{
          return "col-md-12"
        }
    }

    getTitle(): string {
        return this.getAttributeTitle(this.attribute)
    }

    getAttributeTitle(attribute: AttributeModel): string {
        let titleOpt = attribute.options.find(opt => opt.key.toLowerCase() == "title")

        if(titleOpt) {
            return titleOpt.value
        }

        return attribute.key
    }

    getError(): string{
      const isValid = this.isValidBase();

      if(isValid){
        return null
      }
      let errorMsg = this.getOptionByKey(this.attribute, "error");

      return errorMsg.toLowerCase() == "false" || !errorMsg ? "*" : "*"
    }

    isRequired(): boolean{
        let requiredOpt = this.getOptionByKey(this.attribute, "required");

        if(requiredOpt) {
            return requiredOpt.toLowerCase() == 'true'
        }

        let envRequiredOpt = this.envOptions
            .find(opt => opt.key.toLowerCase() == "required")

        if(envRequiredOpt) {
            return envRequiredOpt.value.toLowerCase() == "true"
        }

        return false
    }

    isValidBase(): boolean {
      let isValid = true;

      if (this.isRequired() &&
        !this.attribute.value) {
        isValid = false;
      }

      if(isValid) {
        const minValueOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "minvalue");

        if(minValueOpt) {
            isValid = +this.attribute.value >= +minValueOpt.value;
        }
      }

      if(isValid) {
        const maxValueOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "maxvalue");

        if(maxValueOpt) {
            isValid = +this.attribute.value <= +maxValueOpt.value;
        }
      }

      if(isValid) {
        const minLengthOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "minlength");

        if(minLengthOpt && this.attribute.value) {
            isValid = this.attribute.value.length >= +minLengthOpt.value;
        }
      }

      if(isValid) {
        const maxLengthOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "maxlength");

        if(maxLengthOpt && this.attribute.value) {
            isValid = this.attribute.value.length <= +maxLengthOpt.value;
        }
      }

      if(isValid) {
        const documentMaskOpt = this.attribute.options
            .find(opt => opt.key.toLowerCase() == "mask" && opt.value.toLowerCase() == "document");

        if(documentMaskOpt) {
          isValid = this.validateDocument(this.attribute.value);
        }
      }

      return isValid;
    }

    public validateDocument(document: string): boolean {
      if (document.length > 11) {
        return this.validateCNPJ(document);
      } else if(document.length > 0) {
        return this.validateCPF(document);
      }
      return false;
    }

    private validateCPF(cpf: string): boolean {
      cpf = cpf.replace(/[^\d]+/g, '');
  
      if (cpf.length !== 11) return false;
  
      let sum = 0;
      let remainder;
  
      if (cpf === '00000000000') return false;
  
      for (let i = 1; i <= 9; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
      }
  
      remainder = (sum * 10) % 11;
  
      if ((remainder === 10) || (remainder === 11)) remainder = 0;
      if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  
      sum = 0;
      for (let i = 1; i <= 10; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
      }
  
      remainder = (sum * 10) % 11;
  
      if ((remainder === 10) || (remainder === 11)) remainder = 0;
      if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  
      return true;
    }
  
    // Método para validar CNPJ
    private validateCNPJ(cnpj: string): boolean {
      cnpj = cnpj.replace(/[^\d]+/g, '');
  
      if (cnpj.length !== 14) return false;
  
      if (cnpj === '00000000000000' ||
          cnpj === '11111111111111' ||
          cnpj === '22222222222222' ||
          cnpj === '33333333333333' ||
          cnpj === '44444444444444' ||
          cnpj === '55555555555555' ||
          cnpj === '66666666666666' ||
          cnpj === '77777777777777' ||
          cnpj === '88888888888888' ||
          cnpj === '99999999999999') {
        return false;
      }
  
      let length = cnpj.length - 2;
      let numbers = cnpj.substring(0, length);
      let digits = cnpj.substring(length);
      let sum = 0;
      let pos = length - 7;
  
      for (let i = length; i >= 1; i--) {
        sum += parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) pos = 9;
      }
  
      let result = sum % 11 < 2 ? 0 : 11 - sum % 11;
      if (result !== parseInt(digits.charAt(0))) return false;
  
      length += 1;
      numbers = cnpj.substring(0, length);
      sum = 0;
      pos = length - 7;
  
      for (let i = length; i >= 1; i--) {
        sum += parseInt(numbers.charAt(length - i)) * pos--;
        if (pos < 2) pos = 9;
      }
  
      result = sum % 11 < 2 ? 0 : 11 - sum % 11;
      if (result !== parseInt(digits.charAt(1))) return false;
  
      return true;
    }

    valueEquals(value: string): boolean {
        return this.attribute.value.toLowerCase() == value.toLowerCase()
    }

    visibilityComponent(mode: string): boolean {
        let isHidden: boolean = false;

        if(this.checkViewMode('edit')) {
          if (mode == 'edit' || mode == 'both') {
            isHidden = false
          } else {
            isHidden = true
          }
        } else if (this.checkViewMode('view')) {
          if (mode == 'edit' || mode == 'both') {
            isHidden = false;
          } else {
            isHidden = true
          }
        }
        return isHidden;
    }
}
